import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import VehicleSafetyCheckPDF from './SafetyCheckPDF'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import BigModal from '../../../../components/Modals/BigModal'
import { Plagiarism } from '@mui/icons-material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

// Result banner tones: a pale tint with text in a dark shade of the same hue,
// so the heading and the date line both stay readable on the colour.
const BANNER_TONES = {
  passed: { bg: '#E7F6EC', accent: '#2E9D5B', title: '#14532D', text: '#2F6B47' },
  advisories: { bg: '#FFF3E0', accent: '#ED8B00', title: '#6B3A00', text: '#8A5413' },
}

// Small reusable stat card
const SummaryStat = ({ label, value, color = 'primary' }) => (
  <Card
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2,
      borderLeft: `5px solid`,
      borderColor: `${color}.main`,
      //   height: '100%',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={`${color}.main`}>
      {value}
    </Typography>
  </Card>
)

const VehicleSafetyCheckReport = () => {
  const [loading, setLoading] = useState(true)
  const [report, setReport] = useState(null)
  const { reportID } = useParams()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { user } = useContext(UserContext)
  const [openPDF, setOpenPDF] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  // Amending or deleting a recorded check is for the Fleet team (enforced
  // server-side).
  const canManage = (user?.employee?.teams ?? []).includes('Fleet')

  const handleDelete = () => {
    setDeleting(true)
    API.delete(`/fleet/vehicle-safety-check/${reportID}`)
      .then((res) => {
        if (res.status !== 200) {
          setDeleting(false)
          return setToast({
            message: res?.data?.error || 'Unable to delete this safety check.',
            severity: 'error',
          })
        }
        setToast({ message: 'Safety Check Deleted' })
        const vehicleID = res.data?.vehicle ?? report?.vehicle?._id
        navigate(vehicleID ? `/fleet/${vehicleID}?opentab=safety` : '/safety-checks')
      })
      .catch(() => {
        setDeleting(false)
        setToast({
          message: 'Unable to delete this safety check.',
          severity: 'error',
        })
      })
  }

  useEffect(() => {
    if (!reportID) return
    API.get(`/fleet/vehicle-safety-check-report/${reportID}`)
      .then((res) => res.status === 200 && setReport(res.data))
      .finally(() => setLoading(false))
  }, [reportID])

  if (loading)
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
      </Stack>
    )

  if (!report)
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Unable to load report.
      </Alert>
    )

  const {
    vehicle,
    testedByEmployee,
    testedBy: testedByExternalValue,
    testStats = {},
  } = report
  const answers = report.answers ?? {}

  const hasAdvisories = testStats.advisoryCount > 0
  const tone = BANNER_TONES[hasAdvisories ? 'advisories' : 'passed']
  const BannerIcon = hasAdvisories ? ReportProblemIcon : CheckCircleIcon

  return (
    <Stack gap={2}>
      {/* ---- PASS / ADVISORY BANNER ---- */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: tone.bg,
          borderLeft: `6px solid ${tone.accent}`,
        }}
      >
        <BannerIcon sx={{ color: tone.accent, fontSize: 32 }} />
        <Box minWidth={0}>
          <Typography variant="h6" fontWeight={700} sx={{ color: tone.title }}>
            {hasAdvisories ? 'Vehicle Passed With Advisories' : 'Vehicle Passed'}
          </Typography>
          <Typography variant="body2" sx={{ color: tone.text }}>
            Completed on {moment(report.testDate).format('DD MMM YYYY')}
            {hasAdvisories &&
              ` · ${testStats.advisoryCount} ${
                testStats.advisoryCount === 1 ? 'advisory' : 'advisories'
              }`}
          </Typography>
        </Box>
      </Stack>

      {/* ---- VEHICLE INFO ---- */}
      <Card sx={{ borderRadius: 3 }}>
        <CardHeader
          title="Inspection Details"
          sx={{ color: 'anBlue.main' }}
          action={
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canManage && (
                <>
                  <Button
                    onClick={() =>
                      navigate(`/fleet/vehicle-safety-check/${reportID}/edit`)
                    }
                    variant="outlined"
                    startIcon={<EditIcon />}
                  >
                    Amend
                  </Button>
                  <Button
                    onClick={() => setConfirmDelete(true)}
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                  >
                    Delete
                  </Button>
                </>
              )}
              <Button
                onClick={() => setOpenPDF(true)}
                variant="outlined"
                color="info"
                endIcon={<Plagiarism />}
              >
                View PDF
              </Button>
            </Stack>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Vehicle
              </Typography>
              <Typography variant="h6">{vehicle?.callSign ?? '—'}</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Date
              </Typography>
              <Typography variant="h6">
                {moment(report.testDate).format('DD MMM YYYY')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Mileage
              </Typography>
              <Typography variant="h6">{report.mileage}</Typography>
            </Grid>

            {testedByEmployee?.userAccount ? (
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Technician
                </Typography>
                <Typography variant="h6">
                  {testedByEmployee.userAccount.firstName}{' '}
                  {testedByEmployee.userAccount.lastName}
                </Typography>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Garage
                  </Typography>
                  <Typography variant="h6">
                    {testedByExternalValue?.garageName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Technician
                  </Typography>
                  <Typography variant="h6">
                    {testedByExternalValue?.technicianName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice No.
                  </Typography>
                  <Typography variant="h6">
                    {testedByExternalValue?.invoiceNumber || '—'}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <BigModal
                open={!!openPDF}
                onClose={() => setOpenPDF(false)}
                style={{ height: '80rem' }}
              >
                <PDFViewer width="100%" height="100%">
                  <VehicleSafetyCheckPDF
                    report={report}
                    orgSettings={orgSettings}
                    printedBy={user}
                  />
                </PDFViewer>
              </BigModal>
              {/* <PDFDownloadLink
                document={
                  <VehicleSafetyCheckPDF
                    report={report}
                    orgSettings={orgSettings}
                    printedBy={user}
                  />
                }
                fileName={`VehicleSafetyCheck_${
                  report.vehicle.callSign
                }_${moment(report.testDate).format('YYYY-MM-DD')}.pdf`}
              >
                {({ loading }) => (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? 'Generating PDF…' : 'Download PDF'}
                  </Button>
                )}
              </PDFDownloadLink> */}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ---- SUMMARY ---- */}
      <Card sx={{ borderRadius: 3 }}>
        <CardHeader title="Summary" sx={{ color: 'anBlue.main' }} />
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryStat
                label="Total Checks"
                value={testStats.totalAnswered}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <SummaryStat
                label="Safe"
                value={testStats.safeCount}
                color="anGreen"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <SummaryStat
                label="N/A"
                value={testStats.naCount}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <SummaryStat
                label="Part Fitted"
                value={testStats.partCount}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <SummaryStat
                label="Advisory"
                value={testStats.advisoryCount}
                color="warning"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ---- CATEGORY SECTIONS ---- */}
      <Stack gap={1}>
        {Object.keys(answers).map((category) => (
          <Accordion
            key={category}
            disableGutters
            // elevation={0}
            square
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" fontWeight={600} color="anBlue.main">
                {category}
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Stack spacing={2}>
                {Object.keys(answers[category]).map((item) => {
                  const a = answers[category][item]

                  const statusColor = a.notApplicable
                    ? '#9e9e9e'
                    : a.advised
                    ? '#ff9800'
                    : a.newPartFitted
                    ? '#2196f3'
                    : a.safe
                    ? '#4caf50'
                    : '#e0e0e0'

                  return (
                    <Card
                      key={item}
                      variant="outlined"
                      sx={{ borderLeft: `5px solid ${statusColor}` }}
                    >
                      <CardContent>
                        <Typography fontWeight={700}>{item}</Typography>

                        <Stack direction="row" spacing={1} mt={1}>
                          {a.safe && (
                            <Chip label="Safe" color="success" size="small" />
                          )}
                          {a.notApplicable && <Chip label="N/A" size="small" />}
                          {a.newPartFitted && (
                            <Chip
                              label="Part Fitted"
                              color="info"
                              size="small"
                            />
                          )}
                          {a.advised && (
                            <Chip
                              label="Advisory"
                              color="warning"
                              size="small"
                            />
                          )}
                        </Stack>

                        {a.comments && a.comments.trim() !== '' && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                            color="text.secondary"
                          >
                            {a.comments}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {/* ---- OVERALL COMMENTS ---- */}
      {report.additionalComments && (
        <Card sx={{ borderRadius: 3 }}>
          <CardHeader title="Additional Comments" />
          <CardContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {report.additionalComments}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ---- Created/Updated ---- */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'right', mt: 2 }}
      >
        Report created {moment(report.createdAt).format('DD MMM YYYY HH:mm')}
        {report.amendedAt &&
          ` · Amended ${moment(report.amendedAt).format('DD MMM YYYY HH:mm')}${
            report.updatedBy?.firstName
              ? ` by ${report.updatedBy.firstName} ${report.updatedBy.lastName}`
              : ''
          }`}
      </Typography>

      <Dialog
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this safety check?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The safety check for {vehicle?.callSign ?? 'this vehicle'} on{' '}
            {moment(report.testDate).format('DD MMM YYYY')} will be permanently
            removed, and the vehicle's last safety check date will fall back
            to the previous one. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default VehicleSafetyCheckReport
